import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { usersGetInvitCodeType, usersValidateInviteCode } from '../apis';
import { LOGIN_ERROR_INTL_PREFIX } from '../constants';

export const useValidateInviteCode = ({
  setValidateResult,
}: {
  setValidateResult: (result: { error: string; code_type: string }) => void;
}) => {
  const t = useTranslations();
  const [codeType, setCodeType] = useState<string>('');
  const { countryLevel } = useStore((state) => ({
    countryLevel: state.countryLevel,
  }));

  const { runAsync: runValidateInviteCodeAsync, loading: runValidateInviteCodeLoading } = useFetch(
    (code: string) => usersValidateInviteCode({ path: { code } }),
    {
      manual: true,
      onError: () => {},
    },
  );

  const { runAsync: getInvitCodeTypeAsync, loading: getInvitCodeTypeLoading } = useFetch(
    (code: string) => usersGetInvitCodeType({ query: { inviter_id: code } }),
    {
      manual: true,
      onSuccess: ({ data }) => {
        setCodeType(data?.code_type || '');
      },
      onError: () => {},
    },
  );

  const validateInviteCode = useCallback(
    async (code: string) => {
      // 串行：先校验普通邀请码；valid 即合法，不再调 GetInvitCodeType。
      // 仅在 valid 为 false 时再请求 code_type，区分「达人分享码」与「真·无效码」。
      const validateRes = await runValidateInviteCodeAsync(code).catch(() => null);
      if (validateRes?.data?.valid) {
        setValidateResult({ error: '', code_type: '' });
        return;
      }
      const typeRes = await getInvitCodeTypeAsync(code).catch(() => null);
      const invitCodeType = typeRes?.data?.code_type || '';
      if (invitCodeType === 'agent_share_viral' || invitCodeType === 'artist_program') {
        if (invitCodeType === 'artist_program') {
          setValidateResult({ error: '', code_type: invitCodeType });
          return;
        }
        if (countryLevel !== 't3') {
          setValidateResult({ error: '', code_type: invitCodeType });
          return;
        }
        setValidateResult({
          error: t(`${LOGIN_ERROR_INTL_PREFIX}inviteCodeCountryInvalid`),
          code_type: invitCodeType,
        });
        return;
      }
      setValidateResult({
        error: t(`${LOGIN_ERROR_INTL_PREFIX}inviteCodeInvalid`),
        code_type: invitCodeType,
      });
    },
    [countryLevel, getInvitCodeTypeAsync, runValidateInviteCodeAsync, setValidateResult, t],
  );

  useEffect(() => {
    if (countryLevel === 't3' && codeType === 'agent_share_viral') {
      setValidateResult({
        error: t(`${LOGIN_ERROR_INTL_PREFIX}inviteCodeCountryInvalid`),
        code_type: 'agent_share_viral',
      });
    }
  }, [countryLevel, codeType, setValidateResult]);

  return {
    validateInviteCode,
    validateInviteCodeLoading: runValidateInviteCodeLoading || getInvitCodeTypeLoading,
  };
};
